import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause, RotateCcw } from 'lucide-react';
import { FULL_BY_Z, resolveElement, toSuperscript } from './atomicData';
import {
  VizCard, NowYouTry, VIZ_BTN_PRIMARY, VIZ_BTN_GHOST, VIZ_FOCUS,
  cueIsActive, cueHighlights, usePrefersReducedMotion, type VizCue,
} from '../vizShared';

// FillingMap — the periodic table redrawn as the ORDER ELECTRONS ARRIVE IN.
//
// The thing students cannot see on a normal periodic table is why the
// configuration jumps around: 4s before 3d, and in periods 6 and 7 a single d
// then all fourteen f then the remaining nine d. Told as a rule ("Madelung",
// "the diagonal") it is something to memorise and forget.
//
// THE INSIGHT THIS WIDGET IS BUILT ON, and it is the instructor's own: lay each
// period out in FILL order and the atomic numbers still run straight up. Every
// slot is exactly one element. So the strip is not a diagram of the rule — it
// IS the period, re-ordered, and counting along it gives both the element and
// its configuration at the same time.
//
//   period 1        ss                                  2 elements
//   periods 2-3     ss      pppppp                      8
//   periods 4-5     ss dddddddddd pppppp                18
//   periods 6-7     ss d ffffffffffffff ddddddddd pppppp 32
//
// Period 6 reads: Cs Ba (6s²) · La (5d¹) · Ce…Lu (4f¹⁴) · Hf…Hg (the other
// nine 5d) · Tl…Rn (6p⁶). The f run sits INSET, so the "jump back" into d is
// something you watch happen rather than a rule you are given.
//
// Everything is computed from the strip model, never hard-coded per element.
// A configuration whose superscripts do not sum to Z is the commonest error in
// this topic, so the widget always shows the running total against Z and lets
// the reader do the check that catches it.

export interface FillingMapConfig {
  title?: string;
  element?: string | number;
  symbol?: string;
  Z?: number;
  autoPlay?: boolean;
}

type L = 's' | 'p' | 'd' | 'f';
interface Seg { n: number; l: L; slots: number; inset?: boolean }

/** Each period in the order electrons actually arrive. */
const PERIODS: { period: number; startZ: number; segs: Seg[] }[] = [
  { period: 1, startZ: 1, segs: [{ n: 1, l: 's', slots: 2 }] },
  { period: 2, startZ: 3, segs: [{ n: 2, l: 's', slots: 2 }, { n: 2, l: 'p', slots: 6 }] },
  { period: 3, startZ: 11, segs: [{ n: 3, l: 's', slots: 2 }, { n: 3, l: 'p', slots: 6 }] },
  { period: 4, startZ: 19, segs: [{ n: 4, l: 's', slots: 2 }, { n: 3, l: 'd', slots: 10 }, { n: 4, l: 'p', slots: 6 }] },
  { period: 5, startZ: 37, segs: [{ n: 5, l: 's', slots: 2 }, { n: 4, l: 'd', slots: 10 }, { n: 5, l: 'p', slots: 6 }] },
  // The 6/7 shape: one d, then the whole f run, then the rest of the d.
  { period: 6, startZ: 55, segs: [
    { n: 6, l: 's', slots: 2 }, { n: 5, l: 'd', slots: 1 },
    { n: 4, l: 'f', slots: 14, inset: true }, { n: 5, l: 'd', slots: 9 }, { n: 6, l: 'p', slots: 6 }] },
  { period: 7, startZ: 87, segs: [
    { n: 7, l: 's', slots: 2 }, { n: 6, l: 'd', slots: 1 },
    { n: 5, l: 'f', slots: 14, inset: true }, { n: 6, l: 'd', slots: 9 }, { n: 7, l: 'p', slots: 6 }] },
];

// A FILLED slot is a TINT carrying ink-coloured text, never a solid colour
// carrying white. The four --ws-* colours here are text tokens: they stay light
// in dark theme so they read as text, which is exactly what stops white from
// sitting on them. Measured, white-on-solid in dark theme: ai 3.68:1,
// danger 3.39:1, success 2.67:1, warning 1.91:1 — all under the 4.5:1 AA floor
// and the last one is barely visible. The same ink on a 30% tint measures
// 8.5-12.5:1 in every theme, so that is what this uses. See
// styles/dangerPairing.test.ts, which polices the danger case app-wide.
//
// The block letter (s/p/d/f) is printed INSIDE each slot as well, so the colour
// is never the only thing carrying the meaning.
const TONE: Record<L, { on: string; off: string; text: string }> = {
  s: { on: 'bg-ws-danger/30 border-ws-danger text-ws-ink', off: 'border-ws-danger/40 text-ws-danger', text: 'text-ws-danger' },
  p: { on: 'bg-ws-success/30 border-ws-success text-ws-ink', off: 'border-ws-success/40 text-ws-success', text: 'text-ws-success' },
  d: { on: 'bg-ws-warning/30 border-ws-warning text-ws-ink', off: 'border-ws-warning/40 text-ws-warning', text: 'text-ws-warning' },
  f: { on: 'bg-ws-ai/30 border-ws-ai text-ws-ink', off: 'border-ws-ai/40 text-ws-ai', text: 'text-ws-ai' },
};

/** One slot = one element. Flattened in fill order across the whole table. */
interface Slot { z: number; n: number; l: L; period: number; indexInSeg: number; inset: boolean }

const SLOTS: Slot[] = (() => {
  const out: Slot[] = [];
  for (const p of PERIODS) {
    let z = p.startZ;
    for (const seg of p.segs) {
      for (let i = 0; i < seg.slots; i += 1) {
        out.push({ z, n: seg.n, l: seg.l, period: p.period, indexInSeg: i, inset: !!seg.inset });
        z += 1;
      }
    }
  }
  return out;
})();

/**
 * The configuration of element Z, read straight off the strip.
 *
 * Subshells are merged by (n, l) — period 6 visits 5d twice, and 5d¹ followed
 * later by 5d⁹ is one 5d¹⁰, which is exactly the thing the inset f run makes
 * visible. Ordered by n then l for the written form, because that is how it is
 * written down even though it is not the order it filled in.
 */
function configFor(z: number) {
  const counts = new Map<string, { n: number; l: L; k: number; order: number }>();
  let order = 0;
  for (const s of SLOTS) {
    if (s.z > z) break;
    const key = `${s.n}${s.l}`;
    const e = counts.get(key);
    if (e) e.k += 1;
    else { counts.set(key, { n: s.n, l: s.l, k: 1, order: order += 1 }); }
  }
  const L_ORDER: L[] = ['s', 'p', 'd', 'f'];
  const written = [...counts.values()].sort((a, b) => a.n - b.n || L_ORDER.indexOf(a.l) - L_ORDER.indexOf(b.l));
  const filled = [...counts.values()].sort((a, b) => a.order - b.order);
  const total = written.reduce((t, c) => t + c.k, 0);
  return { written, filled, total };
}

const NOBLE = [2, 10, 18, 36, 54, 86];

function fmt(parts: { n: number; l: L; k: number }[]): string {
  return parts.map((c) => `${c.n}${c.l}${toSuperscript(c.k)}`).join(' ');
}

export default function FillingMap({
  config = {}, cue, onComplete,
}: { config?: FillingMapConfig; cue?: VizCue; onComplete?: (r: unknown) => void }) {
  const reduced = usePrefersReducedMotion();
  const start = useMemo(() => {
    const el = resolveElement(config.element ?? config.symbol ?? config.Z ?? 'Fe');
    return el ? el.z : 26;
  }, [config.element, config.symbol, config.Z]);

  const [z, setZ] = useState(start);
  const [playing, setPlaying] = useState(config.autoPlay !== false && !reduced);
  const [demoed, setDemoed] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => { setZ(start); }, [start]);

  // A cue can drive the element from narration: highlight a symbol, or ramp
  // progress 0..1 across the whole table.
  useEffect(() => {
    if (!cueIsActive(cue)) return;
    const hi = cueHighlights(cue);
    for (const h of hi) {
      const el = resolveElement(h);
      if (el) { setZ(el.z); setPlaying(false); return; }
    }
    const prog = (cue as { progress?: number } | undefined)?.progress;
    if (typeof prog === 'number') { setZ(Math.max(1, Math.round(prog * 118))); setPlaying(false); }
  }, [cue]);

  useEffect(() => {
    if (!playing) return undefined;
    timer.current = window.setInterval(() => {
      setZ((cur) => {
        if (cur >= 118) { setPlaying(false); setDemoed(true); return 118; }
        return cur + 1;
      });
    }, reduced ? 120 : 45);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [playing, reduced]);

  const el = FULL_BY_Z[z];
  const { written, filled, total } = useMemo(() => configFor(z), [z]);
  const core = useMemo(() => {
    const prev = [...NOBLE].reverse().find((n) => n < z);
    return prev ? FULL_BY_Z[prev] : null;
  }, [z]);
  const coreConfig = useMemo(() => (core ? configFor(core.z) : null), [core]);
  const valence = useMemo(() => {
    if (!coreConfig) return written;
    const inCore = new Set(coreConfig.written.map((c) => `${c.n}${c.l}`));
    return filled.filter((c) => !inCore.has(`${c.n}${c.l}`));
  }, [coreConfig, filled, written]);

  const last = filled[filled.length - 1];

  useEffect(() => {
    if (demoed) onComplete?.({ type: 'filling_map', z });
  }, [demoed]); // eslint-disable-line react-hooks/exhaustive-deps

  const step = (d: number) => { setPlaying(false); setZ((c) => Math.min(118, Math.max(1, c + d))); };

  return (
    <VizCard
      title={config.title || 'How the table fills'}
      eyebrow="Atomic structure"
      subtitle="Each row is one period, laid out in the order electrons actually arrive. Every square is one element."
      reducedMotion={reduced}
      actions={(
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className={`${VIZ_BTN_GHOST} ${VIZ_FOCUS}`} onClick={() => step(-1)} aria-label="Previous element">
            <ChevronLeft size={15} />
          </button>
          <button type="button" className={`${VIZ_BTN_PRIMARY} ${VIZ_FOCUS}`} onClick={() => setPlaying((p) => !p)}>
            {playing ? <Pause size={14} /> : <Play size={14} />} {playing ? 'Pause' : 'Play'}
          </button>
          <button type="button" className={`${VIZ_BTN_GHOST} ${VIZ_FOCUS}`} onClick={() => step(1)} aria-label="Next element">
            <ChevronRight size={15} />
          </button>
          <button type="button" className={`${VIZ_BTN_GHOST} ${VIZ_FOCUS}`} onClick={() => { setPlaying(false); setZ(start); }} aria-label="Reset">
            <RotateCcw size={14} />
          </button>
        </div>
      )}
    >
      {/* ── the element, and its configuration read off the strip ─────────── */}
      <div className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-2">
        <div>
          <div className="font-mono text-4xl font-bold leading-none text-ws-ink">{el?.symbol ?? '?'}</div>
          <div className="mt-1 text-xs text-ws-ink-3">{el?.name} · Z = {z}</div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-mono text-sm leading-relaxed text-ws-ink" aria-label="full configuration">
            {written.map((c) => (
              <span key={`${c.n}${c.l}`} className={`${TONE[c.l].text} mr-1.5 font-semibold`}>
                {c.n}{c.l}{toSuperscript(c.k)}
              </span>
            ))}
          </p>
          {core && (
            <p className="mt-1 font-mono text-sm text-ws-ink-2">
              <span className="rounded bg-ws-surface-2 px-1 font-semibold">[{core.symbol}]</span>{' '}
              {fmt(valence)}
            </p>
          )}
          {/* The check that catches the commonest error in this topic. */}
          <p className="mt-1 text-xs text-ws-ink-3">
            superscripts add to <b className={total === z ? 'text-ws-success' : 'text-ws-danger'}>{total}</b>
            {' '}· atomic number is <b>{z}</b>
            {total === z ? ' ✓' : ' — these must match'}
          </p>
        </div>
      </div>

      {/* ── the strips ────────────────────────────────────────────────────── */}
      {/* No overflow-x here on purpose. Periods 6 and 7 are 32 cells wide and a
          horizontal scrollbar put their last 6p cells off-screen -- on a
          projector nobody scrolls sideways, so the end of the row may as well
          not exist. The cells shrink to fit the widest row instead, which is
          also why every row now lines up under the one above it. */}
      <div className="space-y-1.5 pb-1">
        {PERIODS.map((p) => {
          let zc = p.startZ;
          return (
            <div key={p.period} className="flex items-center gap-1.5">
              <span className="w-10 shrink-0 text-right text-[11px] font-medium text-ws-ink-3">P{p.period}</span>
              <div className="flex min-w-0 flex-1 gap-1">
                {p.segs.map((seg, si) => {
                  const cells = [];
                  for (let i = 0; i < seg.slots; i += 1) {
                    const cz = zc;
                    zc += 1;
                    const on = cz <= z;
                    const isNow = cz === z;
                    cells.push(
                      <button
                        type="button"
                        key={cz}
                        onClick={() => { setPlaying(false); setZ(cz); }}
                        title={`${FULL_BY_Z[cz]?.symbol ?? ''} ${FULL_BY_Z[cz]?.name ?? ''} — Z ${cz} — fills ${seg.n}${seg.l}`}
                        aria-label={`${FULL_BY_Z[cz]?.name ?? 'element'} ${cz}, fills ${seg.n}${seg.l}`}
                        className={`h-5 w-full min-w-[11px] max-w-5 shrink rounded-[3px] border text-[9px] font-semibold leading-none transition
                          ${on ? TONE[seg.l].on : `bg-ws-surface ${TONE[seg.l].off}`}
                          ${isNow ? 'ring-2 ring-ws-ink ring-offset-1' : ''} ${VIZ_FOCUS}`}
                      >
                        {seg.l}
                      </button>,
                    );
                  }
                  return (
                    <div
                      key={`${p.period}-${si}`}
                      className={`flex min-w-0 shrink gap-[2px] rounded ${seg.inset ? 'mx-1 rounded-md border border-dashed border-ws-ai/50 bg-ws-ai/5 px-1 py-[2px]' : ''}`}
                      title={seg.inset ? 'the f block — filled BEFORE the rest of the d' : undefined}
                    >
                      {cells}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-2 text-xs text-ws-ink-3">
        The dashed box is the <b className="text-ws-ai">f block</b>. In periods 6 and 7 one{' '}
        <b className="text-ws-warning">d</b> fills, then all fourteen <b className="text-ws-ai">f</b>,
        then the remaining nine <b className="text-ws-warning">d</b> — that jump back is why a heavy
        element&rsquo;s configuration looks out of order.
      </p>

      {/* ── what just filled, and the group rule ──────────────────────────── */}
      {last && (
        <div className="mt-3 rounded-md border-l-[3px] border-ws-accent bg-ws-surface-2 px-3 py-2 text-sm text-ws-ink">
          <b className="font-mono">{el?.symbol}</b> lands in the{' '}
          <span className={`font-semibold ${TONE[last.l].text}`}>{last.n}{last.l}</span> block.
          {' '}Its last electron is number <b>{last.k}</b> of {last.l === 's' ? 2 : last.l === 'p' ? 6 : last.l === 'd' ? 10 : 14}
          {' '}in that subshell, so the configuration ends{' '}
          <b className="font-mono">{last.n}{last.l}{toSuperscript(last.k)}</b>.
        </div>
      )}

      {/* ── the question the lesson kept getting ──────────────────────────── */}
      <details className="mt-3 rounded-md border border-ws-border bg-ws-surface px-3 py-2">
        <summary className={`cursor-pointer text-sm font-semibold text-ws-ink ${VIZ_FOCUS}`}>
          If 1s is closer to the nucleus, why is 2s higher energy?
        </summary>
        <div className="mt-2 space-y-2 text-sm text-ws-ink-2">
          <p>
            Both things are true at once. <b>n</b> (the 1, 2, 3&hellip;) is roughly how far out the
            electron spends its time. Further out means less tightly held by the nucleus, and
            &ldquo;less tightly held&rdquo; is the same sentence as <b>higher energy</b> &mdash; energy here
            means how much it would take to pull the electron off, counted from zero at infinity.
          </p>
          <p>
            So 1s is both <i>closer</i> and <i>lower</i>. There is no contradiction: closer to the
            nucleus <em>is</em> lower energy. An atom with 2s electrons still has its 1s filled
            underneath &mdash; 2s is the next floor up, not a replacement.
          </p>
          <p>
            Within one n, <b>s &lt; p &lt; d &lt; f</b>, because an s electron gets closest to the
            nucleus and feels its pull most. That gap is why 4s fills before 3d: by n = 3 the
            subshell gaps have grown enough that 4s slips underneath 3d. Every jump on the map
            above is that same effect.
          </p>
        </div>
      </details>

      <NowYouTry show={demoed} label="Now you try — click any square" />
    </VizCard>
  );
}
